import React, { useEffect, useMemo, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Loader2, CalendarDays } from 'lucide-react'
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label
} from '../ui'
import { useAuth } from '../../hooks/useAuth'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'
import {
  assignProgramaToSesiones,
  getAllGroups,
  getStaffGroups
} from '../../lib/supabase'
import { getMexicoDateString } from '../../lib/dateUtils'

const getToday = () => getMexicoDateString()

const AsignacionModal = ({
  open,
  onOpenChange,
  programa = null,
  defaultGroupId = null,
  onAssigned
}) => {
  const { staff } = useAuth()
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })
  const [grupos, setGrupos] = useState([])
  const [grupoIds, setGrupoIds] = useState(defaultGroupId ? [defaultGroupId] : [])
  const [fechas, setFechas] = useState([getToday()])
  const [fechaNueva, setFechaNueva] = useState(getToday())
  const [relojCompartido, setRelojCompartido] = useState(false)
  const [notas, setNotas] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!open) return
      setLoading(true)
      const isAdmin = staff?.rol === 'admin' || staff?.rol === 'head_coach' || staff?.rol === 'platform_admin'
      let res
      if (isAdmin) {
        res = await getAllGroups()
      } else if (staff?.id) {
        const { data, error: err } = await getStaffGroups(staff.id)
        res = {
          data: (data || []).map((sg) => sg.grupos).filter(Boolean),
          error: err
        }
      } else {
        res = { data: [], error: null }
      }
      if (cancelled) return
      setGrupos(res.data || [])
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [open, staff?.id, staff?.rol])

  useEffect(() => {
    if (open) {
      setGrupoIds(defaultGroupId ? [defaultGroupId] : [])
      setFechas([getToday()])
      setFechaNueva(getToday())
      setRelojCompartido(false)
      setNotas('')
      setError(null)
      guard.resetDirty()
    }
  }, [open, defaultGroupId]) // eslint-disable-line react-hooks/exhaustive-deps

  const duracionTotalMin = useMemo(() => {
    if (!programa?.secciones?.length) return null
    return Math.floor(programa.secciones.reduce((s, x) => s + (x.duracion_seg || 0), 0) / 60)
  }, [programa])

  const toggleGrupo = (id) => {
    setGrupoIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    )
  }

  const agregarFecha = () => {
    if (!fechaNueva) return
    if (fechas.includes(fechaNueva)) return
    guard.markDirty()
    setFechas((prev) => [...prev, fechaNueva].sort())
  }

  const quitarFecha = (fecha) => {
    guard.markDirty()
    setFechas((prev) => prev.filter((f) => f !== fecha))
  }

  const handleAsignar = async () => {
    if (!fechas.length) {
      setError('Agrega al menos una fecha')
      return
    }
    if (!grupoIds.length) {
      setError('Selecciona al menos un grupo')
      return
    }

    setSaving(true)
    setError(null)
    const { error: err } = await assignProgramaToSesiones({
      programaId: programa.id,
      fechas,
      grupoIds,
      relojCompartido: relojCompartido && grupoIds.length >= 2,
      staffId: staff?.id,
      notas: notas.trim() || null
    })
    setSaving(false)
    if (err) {
      setError(err.message || 'No pudimos asignar el programa')
      return
    }
    if (onAssigned) onAssigned()
    guard.resetDirty()
    onOpenChange(false)
  }

  const tieneSecciones = programa?.secciones?.length > 0
  const showRelojCompartido = grupoIds.length >= 2

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Asignar programa: {programa?.titulo}</DialogTitle>
          <DialogDescription>
            {tieneSecciones ? (
              <span>⏱ {duracionTotalMin} min · {programa.secciones.length} secciones</span>
            ) : (
              <span>Programa sin secciones (solo material)</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-3">
          <div>
            <Label>Fechas</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="date"
                value={fechaNueva}
                onChange={(e) => setFechaNueva(e.target.value)}
              />
              <Button variant="outline" onClick={agregarFecha}>
                <CalendarDays className="h-4 w-4 mr-1" />
                Agregar
              </Button>
            </div>
            <div className="mt-2 space-y-1">
              {fechas.map((f) => (
                <div key={f} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm">
                  <span>{format(parseISO(f), "EEEE d 'de' MMMM", { locale: es })}</span>
                  <Button size="sm" variant="ghost" onClick={() => quitarFecha(f)}>
                    Quitar
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Grupos destino</Label>
            {loading ? (
              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando grupos...
              </div>
            ) : grupos.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-1">No hay grupos disponibles</p>
            ) : (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-border p-2">
                {grupos.map((g) => (
                  <label key={g.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted/50">
                    <input
                      type="checkbox"
                      checked={grupoIds.includes(g.id)}
                      onChange={() => toggleGrupo(g.id)}
                      className="rounded border-input"
                    />
                    <span>{g.nombre_del_grupo}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {showRelojCompartido && (
            <div className="rounded-md border border-border bg-muted/50 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={relojCompartido}
                  onChange={(e) => setRelojCompartido(e.target.checked)}
                  className="mt-0.5 rounded border-input"
                />
                <div className="text-sm">
                  <p className="font-medium text-foreground">Reloj compartido entre grupos</p>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    Cuando un coach presiona Iniciar, el cronometro arranca para todos los grupos seleccionados al mismo tiempo. Util cuando entrenan juntos en cancha. Si lo desactivas, cada grupo arranca su propio reloj.
                  </p>
                  {fechas.length > 1 && (
                    <p className="text-muted-foreground text-xs mt-1 italic">
                      Si compartes reloj con varias fechas, cada fecha tiene su propio token (los grupos se sincronizan dentro de una misma fecha, no entre fechas distintas).
                    </p>
                  )}
                </div>
              </label>
            </div>
          )}

          <div>
            <Label htmlFor="notas">Notas (opcional)</Label>
            <Input
              id="notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Notas internas para esta asignación"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => guard.onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleAsignar} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Asignar
          </Button>
        </DialogFooter>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default AsignacionModal
